import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import "leaflet/dist/leaflet.css";
import "../../assets/css/RouteForm.css";
import { useNavigate, useLocation } from "react-router-dom";
import NavbarMenu from "../heroSection/NavbarMenu";
import Footer from "../footer/Footer";
import DeliverySummary from "./DeliverySummary";
import { userAPI } from "../../api/apiSystem";
import DynamicForm from "./DynamicForm";
import { transportConfigs } from "../transportTypes";
import LocationAutocompleteTransport from "../LocationAutocompleteTransport";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { RouteMap } from "../RouteMap";
import { FaPlus, FaTimes } from "react-icons/fa";

const DATE_TIME_STEP_SECONDS = 15 * 60;
const DEFAULT_TIME = "08:00";

const padTwoDigits = (value) => String(value).padStart(2, "0");

const formatLocalDateTime = (date) => {
  const datePart = [
    date.getFullYear(),
    padTwoDigits(date.getMonth() + 1),
    padTwoDigits(date.getDate()),
  ].join("-");
  const timePart = `${padTwoDigits(date.getHours())}:${padTwoDigits(date.getMinutes())}`;

  return `${datePart}T${timePart}`;
};

const parseLocalDateTime = (value) => {
  if (!value) return null;

  const [datePart, timePart] = value.split("T");
  if (!datePart || !timePart) return null;

  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);

  if ([year, month, day, hour, minute].some(Number.isNaN)) return null;

  return new Date(year, month - 1, day, hour, minute, 0, 0);
};

const roundUpToQuarterHour = (date) => {
  const rounded = new Date(date);
  rounded.setSeconds(0, 0);

  const minutes = rounded.getMinutes();
  const roundedMinutes = Math.ceil(minutes / 15) * 15;

  if (roundedMinutes === 60) {
    rounded.setHours(rounded.getHours() + 1, 0, 0, 0);
  } else {
    rounded.setMinutes(roundedMinutes);
  }

  return rounded;
};

const roundToNearestQuarterHour = (date) => {
  const rounded = new Date(date);
  rounded.setSeconds(0, 0);

  const minutes = rounded.getMinutes();
  const roundedMinutes = Math.round(minutes / 15) * 15;

  if (roundedMinutes === 60) {
    rounded.setHours(rounded.getHours() + 1, 0, 0, 0);
  } else {
    rounded.setMinutes(roundedMinutes);
  }

  return rounded;
};

const getMinDateTimeValue = () =>
  formatLocalDateTime(roundUpToQuarterHour(new Date()));

const getDefaultDateTimeValue = () => {
  const now = new Date();
  const minDate = roundUpToQuarterHour(now);
  const defaultDate = new Date(now);
  defaultDate.setHours(8, 0, 0, 0);

  if (defaultDate < minDate) {
    defaultDate.setDate(defaultDate.getDate() + 1);
  }

  return formatLocalDateTime(defaultDate);
};

export default function RouteForm({ initialData }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { transportType, locations, itemsData, additionalData } =
    initialData || location.state || {};

  const [currentStep, setCurrentStep] = useState(1);
  const [formState, setFormState] = useState({
    booking_from: locations?.booking_from || "",
    booking_to: locations?.booking_to || "",
    from_lat_long: locations?.from_lat_long || "",
    to_lat_long: locations?.to_lat_long || "",
    price_offer: locations?.price_offer || "",
    is_price_negotiable: locations?.is_price_negotiable || "",
    payment_term: locations?.payment_term || "",
    pickupStartDateTime: "",
    pickupEndDateTime: "",
    deliveryStartDateTime: "",
    deliveryEndDateTime: "",
    details: {
      items:
        itemsData && Array.isArray(itemsData) && itemsData.length > 0
          ? itemsData
          : [{}],
      ...additionalData,
    },
  });

  const [coordinates, setCoordinates] = useState({
    from: locations?.from_lat_long
      ? parseCoordinates(locations.from_lat_long)
      : null,
    to: locations?.to_lat_long ? parseCoordinates(locations.to_lat_long) : null,
  });

  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const minDateTime = getMinDateTimeValue();

  const steps = [
    { title: t("route_form.steps.route"), id: 1 },
    { title: t("route_form.steps.details"), id: 2 },
    { title: t("route_form.steps.summary"), id: 3 },
  ];

  const config = transportConfigs.find((c) => c.type === transportType?.name);

  const [showPickupEnd, setShowPickupEnd] = useState(false);
const [showDeliveryStart, setShowDeliveryStart] = useState(false);
const [showDeliveryEnd, setShowDeliveryEnd] = useState(false);

  function parseCoordinates(coordString) {
    if (!coordString) return null;
    try {
      const [lat, lng] = coordString
        .split(",")
        .map((coord) => parseFloat(coord.trim()));
      return [lat, lng];
    } catch (error) {
      console.error("Error parsing coordinates:", error);
      return null;
    }
  }

  const handleLocationSelect = (name, address, lat, lng) => {
    const latLngString = `${lat},${lng}`;

    setFormState((prev) => ({
      ...prev,
      [name]: address,
      [`${name}_lat_long`]: latLngString,
    }));

    setCoordinates((prev) => ({
      ...prev,
      [name === "booking_from" ? "from" : "to"]: [lat, lng],
    }));
  };

  useEffect(() => {
    console.log("Coordinates updated:", coordinates);
  }, [coordinates]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  const handleNext = () => {
    if (currentStep === 1) {
      // التحقق من صحة الخطوة الأولى
      const isValid = validateRouteStep();
      // في handleNext
      if (!isValid) {
        // عرض تنبيه سريع
        Swal.fire({
          icon: "warning",
          // title: t("route_form.validation_error"),
          text: t("route_form.error_message"),
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        
        // التمرير إلى أول حقل به خطأ
        const firstErrorField = Object.keys(validationErrors)[0];
        if (firstErrorField) {
          const element = document.getElementById(`form-${firstErrorField}`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            element.focus();
            // إضافة كلاس للتسليط
            element.classList.add("field-error-highlight");
            setTimeout(() => {
              element.classList.remove("field-error-highlight");
            }, 2000);
          }
        }
        return;
      }
    }
    
    if (currentStep < steps.length) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleStepClick = (stepId) => {
    if (stepId < currentStep) {
      setCurrentStep(stepId);
    }
  };

  const handleDetailsNext = (detailsData) => {
    const splitDateTime = (dateTime) => {
      if (!dateTime) return { date: "", time: "" };
      const [date, timeWithSeconds] = dateTime.split("T");
      const time = timeWithSeconds ? timeWithSeconds.substring(0, 5) : "";

      const [year, month, day] = date.split("-");
      const formattedDate = `${day}.${month}.${year}`;

      return { date: formattedDate, time };
    };

    const pickupStart = splitDateTime(formState.pickupStartDateTime);
    const pickupEnd = splitDateTime(formState.pickupEndDateTime);
    const deliveryStart = splitDateTime(formState.deliveryStartDateTime);
    const deliveryEnd = splitDateTime(formState.deliveryEndDateTime);

    setFormState((prev) => ({
      ...prev,
      details: detailsData,
      pickupStartDate: pickupStart.date,
      pickupEndDate: pickupEnd.date,
      pickupStartTime: pickupStart.time,
      pickupEndTime: pickupEnd.time,
      deliveryStartDate: deliveryStart.date,
      deliveryEndDate: deliveryEnd.date,
      deliveryStartTime: deliveryStart.time,
      deliveryEndTime: deliveryEnd.time,
    }));

    handleNext();
  };

  const handleEditRoute = () => {
    setCurrentStep(1);
  };

  const handleEditItems = () => {
    setCurrentStep(2);
  };

  const handleLocationChange = (e) => {
    if (e.lat && e.lng) {
      const name = e.target.name;
      const value = e.target.value;
      handleLocationSelect(name, value, e.lat, e.lng);
      // التحقق الفوري بعد التحديث
      setTimeout(() => validateField(name, value), 100);
    } else {
      const name = e.target.name;
      const value = e.target.value;
      setFormState((prev) => ({
        ...prev,
        [name]: value,
      }));
      // التحقق الفوري
      validateField(name, value);
    }
  };

  const clearDateTimeError = (fieldName) => {
    setValidationErrors((prev) => {
      if (!prev[fieldName]) return prev;

      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const handleDateTimeFocus = (fieldName) => {
    setFormState((prev) => {
      if (prev[fieldName]) return prev;
      return { ...prev, [fieldName]: getDefaultDateTimeValue() };
    });
  };

  const handleDateTimeChange = (fieldName, value) => {
  const datePart = value?.split("T")?.[0];

  setFormState((prev) => ({
    ...prev,
    [fieldName]:
      !prev[fieldName] && datePart ? `${datePart}T${DEFAULT_TIME}` : value,
  }));
  
  // التحقق الفوري بعد التحديث
  setTimeout(() => validateField(fieldName, value), 100);
};

  const handleDateTimeBlur = (fieldName, value) => {
    const parsedDate = parseLocalDateTime(value);
    if (!parsedDate) return;

    const minDate = roundUpToQuarterHour(new Date());
    const roundedDate = roundToNearestQuarterHour(parsedDate);
    const nextDate = roundedDate < minDate ? minDate : roundedDate;

    setFormState((prev) => ({
      ...prev,
      [fieldName]: formatLocalDateTime(nextDate),
    }));
  };

  const splitDateTime = (dateTime) => {
    if (!dateTime) return { date: "", time: "" };
    const [date, timeWithSeconds] = dateTime.split("T");
    const time = timeWithSeconds ? timeWithSeconds.substring(0, 5) : "";
    return { date, time };
  };

  const getValueOrSkip = (value, formData, fieldName) => {
    if (value !== null && value !== undefined && value !== "") {
      formData.append(fieldName, value);
    }
  };

  const appendBoolean = (value, formData, fieldName) => {
    formData.append(fieldName, toBoolean(value) ? "1" : "0");
  };

  const toBoolean = (value) => {
    if (typeof value === "boolean") return value;
    return ["ja", "yes", "true", "1"].includes(String(value).toLowerCase());
  };

  const toNumber = (value, fallback = 0) => {
    const match = String(value ?? "").match(/[\d.,]+/);
    if (!match) return fallback;
    const numeric = match[0].includes(",")
      ? match[0].replace(/\./g, "").replace(",", ".")
      : match[0].replace(/,/g, "");
    const result = Number(numeric);
    return Number.isFinite(result) ? result : fallback;
  };

  const toWeightNumber = (value) => {
    const text = String(value ?? "");
    const number = toNumber(text, 0);
    return /tonne|tonnen/i.test(text) ? number * 1000 : number;
  };

  const appendSpecialFields = (details, formData) => {
    const specialItems = details.special_items;
    if (!specialItems) return;

    const appendIndexed = (name, values) => {
      (values || []).forEach((value, index) => {
        if (value !== null && value !== undefined && value !== "") {
          formData.append(`${name}[${index}]`, value);
        }
      });
    };

    if (specialItems.move_type === "Moebel_Einzelne_Gegenstaende") {
      appendIndexed("item_weight", (specialItems.items || []).map((item) =>
        toNumber(item.weight || item.size, 0),
      ));
      appendBoolean(
        specialItems.heavyAnswer === "Ja",
        formData,
        "item_breakable",
      );
      getValueOrSkip(
        specialItems.heavy_items?.find((item) => item.description)?.description,
        formData,
        "item_breakable_details",
      );
      appendBoolean(
        specialItems.assemblyAnswer === "Ja",
        formData,
        "special_treatment",
      );
      getValueOrSkip(specialItems.assemblyNotes, formData, "special_treatment_details");
    }

    if (specialItems.move_type === "Baumaterial") {
      appendIndexed(
        "item_weight",
        (specialItems.materials || []).map((material) =>
          Math.round(
            toNumber(material.total_weight_kg || material.weight_per_unit_kg, 0),
          ),
        ),
      );
      getValueOrSkip(
        specialItems.loading_service?.service || "",
        formData,
        "load_helping",
      );
      appendBoolean(
        specialItems.demontageAnswer === "Ja",
        formData,
        "special_treatment",
      );
      getValueOrSkip(specialItems.demontageNotes, formData, "special_treatment_details");
      getValueOrSkip(
        specialItems.pickup?.floor || "",
        formData,
        "loading_floor",
      );
      getValueOrSkip(
        specialItems.delivery?.floor || "",
        formData,
        "unloading_floor",
      );
      appendBoolean(
        toBoolean(specialItems.pickup?.freight_elevator),
        formData,
        "loading_elevator",
      );
      appendBoolean(
        toBoolean(specialItems.delivery?.freight_elevator),
        formData,
        "unloading_elevator",
      );
    }

    if (specialItems.move_type === "Reifen") {
      appendIndexed(
        "item_weight",
        (specialItems.tires || []).map((tire) => Math.round(toNumber(tire.weight, 0))),
      );
      getValueOrSkip(specialItems.loading_help || "", formData, "load_helping");
      getValueOrSkip(specialItems.location?.floor || "", formData, "loading_floor");
      appendBoolean(
        toBoolean(specialItems.location?.elevator),
        formData,
        "loading_elevator",
      );
    }

    const normalizeLocation = (location) => ({
      ...location,
      freight_elevator: toBoolean(location?.freight_elevator),
      direct_access: toBoolean(location?.direct_access),
    });
    const normalizedSpecialItems = {
      ...specialItems,
      materials: (specialItems.materials || []).map((material) => ({
        ...material,
        quantity: material.quantity == null ? 0 : toNumber(material.quantity),
        weight_per_unit_kg: material.weight_per_unit_kg
          ? toWeightNumber(material.weight_per_unit_kg)
          : undefined,
        total_weight_kg: material.total_weight_kg
          ? toWeightNumber(material.total_weight_kg)
          : undefined,
      })),
      pickup: specialItems.pickup ? normalizeLocation(specialItems.pickup) : specialItems.pickup,
      delivery: specialItems.delivery ? normalizeLocation(specialItems.delivery) : specialItems.delivery,
    };
    formData.set("special_items", JSON.stringify(normalizedSpecialItems));
  };

  const handleSubmit = async (finalData = null) => {
    setLoading(true);
    try {
      const data = finalData || formState;

      const pickupStart = splitDateTime(data.pickupStartDateTime);
      const pickupEnd = splitDateTime(data.pickupEndDateTime);
      const deliveryStart = splitDateTime(data.deliveryStartDateTime);
      const deliveryEnd = splitDateTime(data.deliveryEndDateTime);

      const formData = new FormData();

      formData.append("transport_type", transportType?.name || "");
      formData.append("booking_from", data.booking_from || "");
      formData.append("booking_to", data.booking_to || "");

      if (data.price_offer !== undefined && data.price_offer !== null && data.price_offer !== "") {
        formData.append("price_offer", data.price_offer);
        formData.append("is_price_negotiable", data.is_price_negotiable ? "1" : "0");
      }

      if (data.payment_term) {
        formData.append("payment_term", data.payment_term);
      }

      getValueOrSkip(pickupStart.date, formData, "loading_date_start");
      getValueOrSkip(pickupEnd.date, formData, "loading_date_end");
      getValueOrSkip(pickupStart.time, formData, "loading_time_start");
      getValueOrSkip(pickupEnd.time, formData, "loading_time_end");
      getValueOrSkip(deliveryStart.date, formData, "unloading_date_start");
      getValueOrSkip(deliveryEnd.date, formData, "unloading_date_end");
      getValueOrSkip(deliveryStart.time, formData, "unloading_time_start");
      getValueOrSkip(deliveryEnd.time, formData, "unloading_time_end");

      if (data.details) {
        if (data.email) {
          formData.append("email", data.email);
        }

        if (data.deliveryAs) {
          formData.append("delivery_as", data.deliveryAs);
        }

        if (data.details.items && Array.isArray(data.details.items)) {
          data.details.items.forEach((item, index) => {
            Object.keys(item).forEach((key) => {
              if (
                item[key] !== null &&
                item[key] !== undefined &&
                item[key] !== ""
              ) {
                formData.append(`${key}[${index}]`, item[key]);
              }
            });
          });
        }

        if (!data.details.special_items) {
          appendBoolean(data.details.fragile, formData, "item_breakable");
        }
        // getValueOrSkip(data.details.fragileDetails, formData, "item_breakable_details");
        if (!data.details.special_items) {
          appendBoolean(
            data.details.specialHandling,
            formData,
            "special_treatment"
          );
        }
        getValueOrSkip(
          data.details.specialHandlingDetails,
          formData,
          "special_treatment_details"
        );
        getValueOrSkip(data.details.fragileDetails, formData, "item_breakable_details");
        getValueOrSkip(data.details.handlingDetails, formData, "special_treatment_details");
        getValueOrSkip(data.details.helpCarry, formData, "load_helping");
        getValueOrSkip(data.details.beladungFloor, formData, "loading_floor");
        getValueOrSkip(
          data.details.entladungFloor,
          formData,
          "unloading_floor"
        );
        if (!data.details.special_items) {
          appendBoolean(data.details.liftBeladung, formData, "loading_elevator");
        }
        if (!data.details.special_items) {
          appendBoolean(
            data.details.liftEntladung,
            formData,
            "unloading_elevator"
          );
        }
        getValueOrSkip(data.details.itemSecret, formData, "item_secret");
        getValueOrSkip(data.details.signature, formData, "signature");
        getValueOrSkip(data.details.driving, formData, "driving[]");

        if (data.details.images && Array.isArray(data.details.images)) {
          data.details.images.forEach((image, index) => {
            if (image instanceof File) {
              formData.append("images[]", image);
            }
          });
        }

        if (data.details.driving && Array.isArray(data.details.driving)) {
          data.details.driving.forEach((value, index) => {
            formData.append(`driving[${index}]`, value);
          });
        }

        // Structured wizard payload from SpecialItemsWizard (Möbel /
        // Baumaterial / Reifen). This is nested/arbitrary-depth JSON, and
        // this form must stay multipart (file uploads), so we send it as a
        // single JSON string field and decode it server-side in
        // TransportRequest::prepareForValidation().
        appendSpecialFields(data.details, formData);

        getValueOrSkip(data.details.description, formData, "description");
        getValueOrSkip(
          data.details.fragileDetails,
          formData,
          "fragile_details"
        );
        // getValueOrSkip(
        //   data.details.handlingDetails,
        //   formData,
        //   "handling_details"
        // );
      }

      const res = await userAPI.transports.store(formData);

      if (res.success) {
        await Swal.fire({
          icon: "success",
          title: t("route_form.success_title"),
          text: t("route_form.success_message"),
          timer: 2000,
          showConfirmButton: false,
        }).then(() => {
          navigate("/thank-you");
        });
      } else {
        throw new Error(res.message || t("route_form.error_message"));
      }
    } catch (err) {
      console.error("Submission error:", err);
      await Swal.fire({
        icon: "error",
        title: t("route_form.error_title"),
        text: err.message || t("route_form.error_message"),
        timer: 2000,
        showConfirmButton: false,
      });
    } finally {
      setLoading(false);
    }
  };

  // دالة التحقق من صحة حقول المسار (الخطوة 1)
  const validateRouteStep = () => {
    const errors = {};
    
    // =============================================
    // 1. التحقق من وجود مواقع التحميل والتنزيل
    // =============================================
    if (!formState.booking_from?.trim()) {
      errors.booking_from = t("route_form.pickup_location_required");
    }
    if (!formState.booking_to?.trim()) {
      errors.booking_to = t("route_form.delivery_location_required");
    }
    
    // =============================================
    // 2. التحقق من وجود تواريخ التحميل (Pickup)
    // =============================================
    
    // 2.1 تاريخ بداية التحميل - إلزامي دائماً
    if (!formState.pickupStartDateTime) {
      errors.pickupStartDateTime = t("route_form.pickup_start_required");
    }
    
    // 2.2 تاريخ نهاية التحميل - مطلوب فقط إذا كان الحقل ظاهراً
    if (showPickupEnd && !formState.pickupEndDateTime) {
      errors.pickupEndDateTime = t("route_form.pickup_end_required");
    }
    
    // =============================================
    // 3. التحقق من صحة تواريخ التحميل
    // =============================================
    // if (formState.pickupStartDateTime) {
    //   const pickupStart = new Date(formState.pickupStartDateTime);
    //   const minDate = new Date();
      
    //   // التحقق من أن تاريخ بداية التحميل ليس في الماضي
    //   if (pickupStart < minDate) {
    //     errors.pickupStartDateTime = t("route_form.pickup_start_past");
    //   }
      
    //   // التحقق من أن تاريخ نهاية التحميل بعد أو يساوي تاريخ البداية
    //   if (showPickupEnd && formState.pickupEndDateTime) {
    //     const pickupEnd = new Date(formState.pickupEndDateTime);
    //     // ✅ تم التعديل: استخدام < بدلاً من <= للسماح بنفس الوقت
    //     if (pickupEnd < pickupStart) {
    //       errors.pickupEndDateTime = t("route_form.pickup_end_after_start");
    //     }
    //   }
    // }
    
    // =============================================
    // 4. التحقق من تواريخ التسليم (Delivery)
    // =============================================
    
    // 4.1 تاريخ بداية التسليم - اختياري
    // if (formState.deliveryStartDateTime) {
    //   const deliveryStart = new Date(formState.deliveryStartDateTime);
    //   const pickupEnd = formState.pickupEndDateTime 
    //     ? new Date(formState.pickupEndDateTime) 
    //     : formState.pickupStartDateTime 
    //       ? new Date(formState.pickupStartDateTime) 
    //       : null;
      
    //   // التحقق من أن بداية التسليم بعد أو يساوي نهاية التحميل
    //   if (pickupEnd && deliveryStart < pickupEnd) {
    //     errors.deliveryStartDateTime = t("route_form.delivery_start_after_pickup_end");
    //   }
    // }
    
    // // 4.2 تاريخ نهاية التسليم - مطلوب فقط إذا كان الحقل ظاهراً
    // if (showDeliveryEnd) {
    //   if (!formState.deliveryEndDateTime) {
    //     errors.deliveryEndDateTime = t("route_form.delivery_end_required");
    //   } else {
    //     const deliveryEnd = new Date(formState.deliveryEndDateTime);
        
    //     // التحقق من وجود بداية التسليم
    //     if (formState.deliveryStartDateTime) {
    //       const deliveryStart = new Date(formState.deliveryStartDateTime);
    //       // ✅ تم التعديل: استخدام < بدلاً من <= للسماح بنفس الوقت
    //       if (deliveryEnd < deliveryStart) {
    //         errors.deliveryEndDateTime = t("route_form.delivery_end_after_start");
    //       }
    //     }
        
    //     // التحقق من أن نهاية التسليم بعد أو يساوي نهاية التحميل
    //     const pickupEnd = formState.pickupEndDateTime 
    //       ? new Date(formState.pickupEndDateTime) 
    //       : formState.pickupStartDateTime 
    //         ? new Date(formState.pickupStartDateTime) 
    //         : null;
        
    //     // ✅ تم التعديل: استخدام < بدلاً من <= للسماح بنفس الوقت
    //     if (pickupEnd && deliveryEnd < pickupEnd) {
    //       errors.deliveryEndDateTime = t("route_form.delivery_end_after_pickup_end");
    //     }
    //   }
    // }
    
    // =============================================
    // 5. التحقق من التناسق بين جميع التواريخ
    // =============================================
    
    // إذا كان deliveryStartDateTime موجوداً بدون deliveryEndDateTime
    if (formState.deliveryStartDateTime && !formState.deliveryEndDateTime && showDeliveryEnd) {
      errors.deliveryEndDateTime = t("route_form.delivery_end_required");
    }
    
    // إذا كان deliveryEndDateTime موجوداً بدون deliveryStartDateTime
    if (formState.deliveryEndDateTime && !formState.deliveryStartDateTime) {
      errors.deliveryStartDateTime = t("route_form.delivery_start_required");
    }
    
    // =============================================
    // 6. تحديث حالة الأخطاء
    // =============================================
    setValidationErrors(errors);
    
    return Object.keys(errors).length === 0;
  };

  // دالة للتحقق من حقل معين عند التغيير
  const validateField = (fieldName, value) => {
    const errors = { ...validationErrors };
    
    switch (fieldName) {
      case "booking_from":
        if (!value?.trim()) {
          errors.booking_from = t("route_form.pickup_location_required");
        } else {
          delete errors.booking_from;
        }
        break;
        
      case "booking_to":
        if (!value?.trim()) {
          errors.booking_to = t("route_form.delivery_location_required");
        } else {
          delete errors.booking_to;
        }
        break;
        
      // case "pickupStartDateTime":
      //   if (!value) {
      //     errors.pickupStartDateTime = t("route_form.pickup_start_required");
      //   } else {
      //     const selectedDate = new Date(value);
      //     const minDate = new Date();
      //     if (selectedDate < minDate) {
      //       errors.pickupStartDateTime = t("route_form.pickup_start_past");
      //     } else {
      //       delete errors.pickupStartDateTime;
      //     }
      //   }
      //   break;
        
      // case "pickupEndDateTime":
      //   if (!value) {
      //     errors.pickupEndDateTime = t("route_form.pickup_end_required");
      //   } else if (formState.pickupStartDateTime) {
      //     const pickupStart = new Date(formState.pickupStartDateTime);
      //     const pickupEnd = new Date(value);
      //     // ✅ تم التعديل: استخدام < بدلاً من <= للسماح بنفس الوقت
      //     if (pickupEnd <= pickupStart) {
      //       errors.pickupEndDateTime = t("route_form.pickup_end_after_start");
      //     } else {
      //       delete errors.pickupEndDateTime;
      //     }
      //   } else {
      //     delete errors.pickupEndDateTime;
      //   }
      //   break;
        
      // case "deliveryStartDateTime":
      //   if (value) {
      //     const deliveryStart = new Date(value);
      //     if (formState.pickupEndDateTime) {
      //       const pickupEnd = new Date(formState.pickupEndDateTime);
      //       // ✅ تم التعديل: استخدام < بدلاً من <= للسماح بنفس الوقت
      //       if (deliveryStart < pickupEnd) {
      //         errors.deliveryStartDateTime = t("route_form.delivery_start_after_pickup_end");
      //       } else {
      //         delete errors.deliveryStartDateTime;
      //       }
      //     } else {
      //       delete errors.deliveryStartDateTime;
      //     }
      //   } else {
      //     delete errors.deliveryStartDateTime;
      //   }
      //   break;
        
      // case "deliveryEndDateTime":
      //   if (value) {
      //     const deliveryEnd = new Date(value);
      //     if (formState.deliveryStartDateTime) {
      //       const deliveryStart = new Date(formState.deliveryStartDateTime);
      //       // ✅ تم التعديل: استخدام < بدلاً من <= للسماح بنفس الوقت
      //       if (deliveryEnd < deliveryStart) {
      //         errors.deliveryEndDateTime = t("route_form.delivery_end_after_start");
      //       } else {
      //         delete errors.deliveryEndDateTime;
      //       }
      //     }
      //     if (formState.pickupEndDateTime) {
      //       const pickupEnd = new Date(formState.pickupEndDateTime);
      //       // ✅ تم التعديل: استخدام < بدلاً من <= للسماح بنفس الوقت
      //       if (deliveryEnd < pickupEnd) {
      //         errors.deliveryEndDateTime = t("route_form.delivery_end_after_pickup_end");
      //       }
      //     }
      //   } else {
      //     delete errors.deliveryEndDateTime;
      //   }
      //   break;
        
      default:
        break;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  return (
    <>
      <NavbarMenu />
      <Container className="py-4">
        <Card className="shadow-lg border-0 rounded-4">
          {/* Stepper Section */}
          <div className="stepper-section p-5">
            <div className="stepper-wrapper position-relative">
              <div className="progress-tracks">
                <div
                  className="progress-fill"
                  style={{
                    width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`,
                  }}
                ></div>
              </div>
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={`step-item ${currentStep === step.id
                      ? "active"
                      : currentStep > step.id
                        ? "completed"
                        : ""
                    }`}
                  style={{
                    left: `${(index / (steps.length - 1)) * 100}%`,
                    transform: "translateX(-50%)",
                  }}
                  onClick={() => handleStepClick(step.id)}
                >
                  <div className="step-circle">
                    {currentStep > step.id ? "✓" : step.id}
                  </div>
                  <div className="step-title">{step.title}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Content Section */}
          <div className="content-section p-4">
            {currentStep === 1 && (
              <>
                <div className="text-center mb-4">
                  <h5 className="fw-bold text-danger">
                    {t("route_form.choose_route")}
                  </h5>
                  <h3 className="fw-bold mt-2">
                    {t("route_form.choose_route_title")}
                  </h3>
                  <p className="text-muted">
                    <strong>{t("route_form.enter_pickup_delivery")}</strong>
                  </p>
                </div>

                <div className="mb-4">
                  <RouteMap
                    fromCoords={coordinates.from}
                    toCoords={coordinates.to}
                    key={`${coordinates.from}-${coordinates.to}`}
                  />
                </div>

                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleNext();
                  }}
                >
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group controlId="formFrom">
                        <Form.Label>{t("route_form.pickup_location")} *</Form.Label>
                        <LocationAutocompleteTransport
                          id="form-booking_from"
                          name="booking_from"
                          value={formState.booking_from}
                          onChange={handleLocationChange}
                          onLocationSelect={handleLocationSelect}
                          required
                          isInvalid={!!validationErrors.booking_from}
                        />
                        {validationErrors.booking_from && (
                          <div className="invalid-feedback d-block text-danger">
                            {validationErrors.booking_from}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group controlId="formTo">
                        <Form.Label>
                          {t("route_form.delivery_location")} *
                        </Form.Label>
                        <LocationAutocompleteTransport
                          name="booking_to"
                          value={formState.booking_to}
                          onChange={handleLocationChange}
                          onLocationSelect={handleLocationSelect}
                          required
                          isInvalid={!!validationErrors.booking_to}
                        />
                        {validationErrors.booking_to && (
                          <div className="invalid-feedback d-block text-danger">
                            {validationErrors.booking_to}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          {t("route_form.pickup_start_datetime")} *
                        </Form.Label>
                        <Form.Control
                          type="datetime-local"
                          value={formState.pickupStartDateTime}
                          onFocus={() =>
                            handleDateTimeFocus("pickupStartDateTime")
                          }
                          onChange={(e) =>
                            handleDateTimeChange(
                              "pickupStartDateTime",
                              e.target.value,
                            )
                          }
                          onBlur={(e) =>
                            handleDateTimeBlur(
                              "pickupStartDateTime",
                              e.target.value,
                            )
                          }
                          min={minDateTime}
                          step={DATE_TIME_STEP_SECONDS}
                          isInvalid={!!validationErrors.pickupStartDateTime}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.pickupStartDateTime}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          {t("route_form.pickup_end_datetime")} *
                        </Form.Label>
                        <Form.Control
                          type="datetime-local"
                          value={formState.pickupEndDateTime}
                          onFocus={() =>
                            handleDateTimeFocus("pickupEndDateTime")
                          }
                          onChange={(e) =>
                            handleDateTimeChange(
                              "pickupEndDateTime",
                              e.target.value,
                            )
                          }
                          onBlur={(e) =>
                            handleDateTimeBlur(
                              "pickupEndDateTime",
                              e.target.value,
                            )
                          }
                          min={minDateTime}
                          step={DATE_TIME_STEP_SECONDS}
                          isInvalid={!!validationErrors.pickupEndDateTime}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.pickupEndDateTime}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          {t("route_form.delivery_start_datetime")}
                        </Form.Label>
                        <Form.Control
                          type="datetime-local"
                          value={formState.deliveryStartDateTime}
                          onFocus={() =>
                            handleDateTimeFocus("deliveryStartDateTime")
                          }
                          onChange={(e) =>
                            handleDateTimeChange(
                              "deliveryStartDateTime",
                              e.target.value,
                            )
                          }
                          onBlur={(e) =>
                            handleDateTimeBlur(
                              "deliveryStartDateTime",
                              e.target.value,
                            )
                          }
                          min={minDateTime}
                          step={DATE_TIME_STEP_SECONDS}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          {t("route_form.delivery_end_datetime")}
                        </Form.Label>
                        <Form.Control
                          type="datetime-local"
                          value={formState.deliveryEndDateTime}
                          onFocus={() =>
                            handleDateTimeFocus("deliveryEndDateTime")
                          }
                          onChange={(e) =>
                            handleDateTimeChange(
                              "deliveryEndDateTime",
                              e.target.value,
                            )
                          }
                          onBlur={(e) =>
                            handleDateTimeBlur(
                              "deliveryEndDateTime",
                              e.target.value,
                            )
                          }
                          min={minDateTime}
                          step={DATE_TIME_STEP_SECONDS}
                        />
                      </Form.Group>
                    </Col>
                  </Row> */}

                  {/* Pickup Start */}
                  {/* ============================================
                  SECTION: PICKUP & DELIVERY DATES
                  ============================================ */}
                  <Row className="mb-3 align-items-start">
                    <Col md={6}>
                      <div className="pickup-section">
                        
                        <Form.Group className="mb-2">
                          <Form.Label>
                            {t("route_form.pickup_start_datetime")} *
                          </Form.Label>
                          <Form.Control
                            id="form-pickupStartDateTime"
                            type="datetime-local"
                            value={formState.pickupStartDateTime}
                            onFocus={() => handleDateTimeFocus("pickupStartDateTime")}
                            onChange={(e) => handleDateTimeChange("pickupStartDateTime", e.target.value)}
                            onBlur={(e) => handleDateTimeBlur("pickupStartDateTime", e.target.value)}
                            min={minDateTime}
                            step={DATE_TIME_STEP_SECONDS}
                            isInvalid={!!validationErrors.pickupStartDateTime}
                          />
                          <Form.Control.Feedback type="invalid">
                            {validationErrors.pickupStartDateTime}
                          </Form.Control.Feedback>
                        </Form.Group>

                        {showPickupEnd && (
                          <Form.Group className="mb-2">
                            <Form.Label>
                              {t("route_form.pickup_end_datetime")} *
                            </Form.Label>
                            <Form.Control
                              id="form-pickupEndDateTime"
                              type="datetime-local"
                              value={formState.pickupEndDateTime}
                              onFocus={() => handleDateTimeFocus("pickupEndDateTime")}
                              onChange={(e) => handleDateTimeChange("pickupEndDateTime", e.target.value)}
                              onBlur={(e) => handleDateTimeBlur("pickupEndDateTime", e.target.value)}
                              min={minDateTime}
                              step={DATE_TIME_STEP_SECONDS}
                              isInvalid={!!validationErrors.pickupEndDateTime}
                            />
                            <Form.Control.Feedback type="invalid">
                              {validationErrors.pickupEndDateTime}
                            </Form.Control.Feedback>
                          </Form.Group>
                        )}

                        <div className="d-flex gap-2 mt-2">
                          {!showPickupEnd ? (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => {
                                setShowPickupEnd(true);
                                if (!formState.pickupEndDateTime) {
                                  const defaultDate = new Date(formState.pickupStartDateTime || new Date());
                                  defaultDate.setHours(defaultDate.getHours() + 1);
                                  handleDateTimeChange("pickupEndDateTime", formatLocalDateTime(defaultDate));
                                }
                              }}
                              style={{ fontSize: "13px" }}
                            >
                              <FaPlus className="me-1" /> {t("route_form.add_pickup_end")}
                            </Button>
                          ) : (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                setShowPickupEnd(false);
                                setFormState((prev) => ({
                                  ...prev,
                                  pickupEndDateTime: "",
                                }));
                                clearDateTimeError("pickupEndDateTime");
                              }}
                              style={{ fontSize: "13px" }}
                            >
                              <FaTimes className="me-1" /> {t("route_form.remove_pickup_end")}
                            </Button>
                          )}
                        </div>
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="delivery-section">
                        
                        <Form.Group className="mb-2">
                          <Form.Label>
                            {t("route_form.delivery_start_datetime")}
                          </Form.Label>
                          <Form.Control
                            id="form-deliveryStartDateTime"
                            type="datetime-local"
                            value={formState.deliveryStartDateTime}
                            onFocus={() => handleDateTimeFocus("deliveryStartDateTime")}
                            onChange={(e) => handleDateTimeChange("deliveryStartDateTime", e.target.value)}
                            onBlur={(e) => handleDateTimeBlur("deliveryStartDateTime", e.target.value)}
                            min={minDateTime}
                            step={DATE_TIME_STEP_SECONDS}
                            isInvalid={!!validationErrors.deliveryStartDateTime}
                          />
                          <Form.Control.Feedback type="invalid">
                            {validationErrors.deliveryStartDateTime}
                          </Form.Control.Feedback>
                        </Form.Group>

                        {showDeliveryEnd && (
                          <Form.Group className="mb-2">
                            <Form.Label>
                              {t("route_form.delivery_end_datetime")}
                            </Form.Label>
                            <Form.Control
                              id="form-deliveryEndDateTime"
                              type="datetime-local"
                              value={formState.deliveryEndDateTime}
                              onFocus={() => handleDateTimeFocus("deliveryEndDateTime")}
                              onChange={(e) => handleDateTimeChange("deliveryEndDateTime", e.target.value)}
                              onBlur={(e) => handleDateTimeBlur("deliveryEndDateTime", e.target.value)}
                              min={minDateTime}
                              step={DATE_TIME_STEP_SECONDS}
                              isInvalid={!!validationErrors.deliveryEndDateTime}
                            />
                            <Form.Control.Feedback type="invalid">
                              {validationErrors.deliveryEndDateTime}
                            </Form.Control.Feedback>
                          </Form.Group>
                        )}

                        <div className="d-flex gap-2 mt-2">
                          {!showDeliveryEnd ? (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => {
                                setShowDeliveryEnd(true);
                                if (!formState.deliveryEndDateTime) {
                                  const defaultDate = new Date(formState.deliveryStartDateTime || formState.pickupEndDateTime || new Date());
                                  defaultDate.setHours(defaultDate.getHours() + 2);
                                  handleDateTimeChange("deliveryEndDateTime", formatLocalDateTime(defaultDate));
                                }
                              }}
                              style={{ fontSize: "13px" }}
                            >
                              <FaPlus className="me-1" /> {t("route_form.add_delivery_end")}
                            </Button>
                          ) : (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                setShowDeliveryEnd(false);
                                setFormState((prev) => ({
                                  ...prev,
                                  deliveryEndDateTime: "",
                                }));
                                clearDateTimeError("deliveryEndDateTime");
                              }}
                              style={{ fontSize: "13px" }}
                            >
                              <FaTimes className="me-1" /> {t("route_form.remove_delivery_end")}
                            </Button>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <div className="text-center">
                    <Button
                      type="submit"
                      className="px-5 rounded-5 text-white"
                      style={{ backgroundColor: "#ff6b00", border: "none" }}
                      disabled={Object.keys(validationErrors).length > 0}
                    >
                      {t("route_form.next")}
                    </Button>
                  </div>
                </Form>
              </>
            )}

            {currentStep === 2 && config && (
              <DynamicForm
                config={config}
                onNext={handleDetailsNext}
                onEditRoute={handleEditRoute}
                initialItems={formState.details || {}}
              />
            )}

            {currentStep === 3 && (
              <div>
                <DeliverySummary
                  formState={formState}
                  onSubmit={handleSubmit}
                  onEditRoute={handleEditRoute}
                  onEditItems={handleEditItems}
                />
              </div>
            )}
          </div>
        </Card>
      </Container>
      <Footer />
    </>
  );
}